/**
 * 生產製造模組路由 — BOM settings、加工廠、工單、分析。
 *
 * 註冊方式：由 adminRoutes.jsx import 合併至 children。
 * hub／各列表父路由掛 read；create／edit／copy／remark／入出庫／異動掛對應 mutation。
 */
import { Navigate } from 'react-router';

import {
  ANALYSIS_READ_CODES,
  BOM_READ_CODES,
  PERM,
  PROCESSING_FACTORY_READ_CODES,
  WORK_ORDER_READ_CODES,
} from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import ProductionHubPage from '#/modules/infra/pages/ProductionHubPage';
import AnalysisShellPage from '#/modules/momMgmt/analysis/pages/AnalysisShellPage';
import BomDetailPage from '#/modules/momMgmt/settings/bom/pages/BomDetailPage';
import BomListPage from '#/modules/momMgmt/settings/bom/pages/BomListPage';
import CreateEditBomPage from '#/modules/momMgmt/settings/bom/pages/CreateEditBomPage';
import CreateEditProcessingFactoryPage from '#/modules/momMgmt/settings/processingFactory/pages/CreateEditProcessingFactoryPage';
import ProcessingFactoryListPage from '#/modules/momMgmt/settings/processingFactory/pages/ProcessingFactoryListPage';
import CreateEditWorkOrderAmendmentPage from '#/modules/momMgmt/workOrder/pages/CreateEditWorkOrderAmendmentPage';
import CreateEditWorkOrderPage from '#/modules/momMgmt/workOrder/pages/CreateEditWorkOrderPage';
import WorkOrderDetailPage from '#/modules/momMgmt/workOrder/pages/WorkOrderDetailPage';
import WorkOrderListPage from '#/modules/momMgmt/workOrder/pages/WorkOrderListPage';
import WorkOrderRemarkPage from '#/modules/momMgmt/workOrder/pages/WorkOrderRemarkPage';
import WorkOrderInboundPage from '#/modules/momMgmt/workOrder/inbound/pages/WorkOrderInboundPage';
import CreateEditWorkOrderOutboundPage from
  '#/modules/momMgmt/workOrder/outbound/pages/CreateEditWorkOrderOutboundPage';
import WorkOrderReturnPage from '#/modules/momMgmt/workOrder/return/pages/WorkOrderReturnPage';
import WorkOrderStockReturnListPage from '#/modules/momMgmt/workOrder/return/pages/WorkOrderStockReturnListPage';
import WorkOrderTransferPage from '#/modules/momMgmt/workOrder/transfer/pages/WorkOrderTransferPage';
import WorkOrderStockTransferListPage from '#/modules/momMgmt/workOrder/transfer/pages/WorkOrderStockTransferListPage';
import CreateEditStockReturnPage from '#/modules/imsMgmt/receipts/stockReturn/pages/CreateEditStockReturnPage';
import StockReturnDetailPage from '#/modules/imsMgmt/receipts/stockReturn/pages/StockReturnDetailPage';
import StockReturnRemarkPage from '#/modules/imsMgmt/receipts/stockReturn/pages/StockReturnRemarkPage';
import StockTransferDetailPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/StockTransferDetailPage';
import StockTransferRemarkPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/StockTransferRemarkPage';

const PRODUCTION_HUB_READ_CODES = [
  ...BOM_READ_CODES,
  ...WORK_ORDER_READ_CODES,
  ...ANALYSIS_READ_CODES,
  ...PROCESSING_FACTORY_READ_CODES,
];

export const productionRoutes = [
  {
    path: 'production',
    handle: { title: '生產製造' },
    children: [
      {
        index: true,
        element: (
          <RequirePermission codes={PRODUCTION_HUB_READ_CODES} mode="any">
            <ProductionHubPage />
          </RequirePermission>
        ),
      },
      {
        path: 'bom-mgmt',
        handle: {
          title: 'BOM列表',
        },
        element: (
          <RequirePermission codes={BOM_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <BomListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增 BOM' },
            element: (
              <RequirePermission code={PERM.BOM_CREATE}>
                <CreateEditBomPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯 BOM' },
            element: (
              <RequirePermission code={PERM.BOM_UPDATE}>
                <CreateEditBomPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/copy',
            handle: { title: '複製 BOM' },
            element: (
              <RequirePermission code={PERM.BOM_CREATE}>
                <CreateEditBomPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: 'BOM 詳情' },
            element: <BomDetailPage />,
          },
        ],
      },
      {
        path: 'factory-mgmt',
        handle: { title: '加工廠列表' },
        element: (
          <RequirePermission codes={PROCESSING_FACTORY_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <ProcessingFactoryListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增加工廠' },
            element: (
              <RequirePermission code={PERM.PROCESSING_FACTORY_CREATE}>
                <CreateEditProcessingFactoryPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯加工廠' },
            element: (
              <RequirePermission code={PERM.PROCESSING_FACTORY_UPDATE}>
                <CreateEditProcessingFactoryPage />
              </RequirePermission>
            ),
          },
        ],
      },
      {
        path: 'order-mgmt',
        handle: {
          title: '工單列表',
        },
        element: (
          <RequirePermission codes={WORK_ORDER_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <WorkOrderListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增工單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_CREATE}>
                <CreateEditWorkOrderPage />
              </RequirePermission>
            ),
          },
          {
            path: 'transfer',
            handle: { title: '工單轉倉' },
            children: [
              {
                index: true,
                element: (
                  <RequirePermission code={PERM.STOCK_TRANSFER_READ}>
                    <WorkOrderStockTransferListPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'create',
                handle: { title: '新增工單轉倉' },
                element: (
                  <RequirePermission code={PERM.STOCK_TRANSFER_CREATE}>
                    <WorkOrderTransferPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/edit',
                handle: { title: '編輯工單轉倉' },
                element: (
                  <RequirePermission code={PERM.STOCK_TRANSFER_UPDATE}>
                    <WorkOrderTransferPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/remark',
                handle: { title: '編輯轉倉備註' },
                element: (
                  <RequirePermission code={PERM.STOCK_TRANSFER_UPDATE}>
                    <StockTransferRemarkPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                handle: { title: '轉倉單詳情' },
                element: (
                  <RequirePermission code={PERM.STOCK_TRANSFER_READ}>
                    <StockTransferDetailPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'return',
            handle: { title: '工單退料' },
            children: [
              {
                index: true,
                element: (
                  <RequirePermission code={PERM.STOCK_RETURN_READ}>
                    <WorkOrderStockReturnListPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'create',
                handle: { title: '新增工單退料' },
                element: (
                  <RequirePermission code={PERM.STOCK_RETURN_CREATE}>
                    <WorkOrderReturnPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/edit',
                handle: { title: '編輯退料單' },
                element: (
                  <RequirePermission code={PERM.STOCK_RETURN_UPDATE}>
                    <CreateEditStockReturnPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/remark',
                handle: { title: '編輯退料備註' },
                element: (
                  <RequirePermission code={PERM.STOCK_RETURN_UPDATE}>
                    <StockReturnRemarkPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                handle: { title: '退料單詳情' },
                element: (
                  <RequirePermission code={PERM.STOCK_RETURN_READ}>
                    <StockReturnDetailPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: ':id/edit',
            handle: { title: '編輯工單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_UPDATE}>
                <CreateEditWorkOrderPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯工單備註' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_UPDATE}>
                <WorkOrderRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/inbound/create',
            handle: { title: '新建入庫單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_CREATE}>
                <WorkOrderInboundPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/inbound/:receiptId/edit',
            handle: { title: '編輯入庫單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_UPDATE}>
                <WorkOrderInboundPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/inbound',
            element: (
              <Navigate to="..?tab=stockReceipts" replace />
            ),
          },
          {
            path: ':id/outbound/create',
            handle: { title: '新建出庫單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_CREATE}>
                <CreateEditWorkOrderOutboundPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/outbound/:issueId/edit',
            handle: { title: '編輯出庫單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_UPDATE}>
                <CreateEditWorkOrderOutboundPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/outbound',
            element: (
              <Navigate to="..?tab=stockIssues" replace />
            ),
          },
          {
            path: ':id/amendment/create',
            handle: { title: '新建異動單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_AMENDMENT_CREATE}>
                <CreateEditWorkOrderAmendmentPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/amendment/:amendmentId/edit',
            handle: { title: '編輯異動單' },
            element: (
              <RequirePermission code={PERM.WORK_ORDER_AMENDMENT_UPDATE}>
                <CreateEditWorkOrderAmendmentPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '工單詳情' },
            element: <WorkOrderDetailPage />,
          },
        ],
      },
      {
        path: 'analysis',
        handle: { title: '分析' },
        element: (
          <RequirePermission codes={ANALYSIS_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <AnalysisShellPage />,
          },
          {
            path: 'orders',
            element: <Navigate to={ROUTES.analysisOrders} replace />,
          },
          {
            path: 'materials',
            element: <Navigate to={ROUTES.analysisMaterials} replace />,
          },
        ],
      },
    ],
  },
];
